/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ТЕКУЩЕЕ МЕРОПРИЯТИЕ — скоуп, состав и **моя роль в нём**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Самый важный стор этого приложения. Всё, что живёт внутри мероприятия
 * (участники, справочник ролей, конструктор полей, импорт), спрашивает права
 * здесь — и больше нигде.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПОЧЕМУ РОЛЬ В МЕРОПРИЯТИИ НЕЛЬЗЯ ВЗЯТЬ ИЗ ТОКЕНА
 * ───────────────────────────────────────────────────────────────────────────
 *
 * В access-токене есть `role`, но это `SystemRole` — `admin` или `user`, на всю
 * систему. Роль **в мероприятии** (`owner` / `viewer_plus` / `viewer`) принадлежит паре
 * (мероприятие, пользователь) и в токен не кладётся намеренно: составы
 * меняются, пока токен жив, а токен живёт 15 минут. Единственный способ её
 * узнать — прочитать состав: `GET /events/{id}/members` доступен любому
 * участнику, включая наблюдателя, потому что «знать, к кому идти за доступом»
 * это чтение, а не полномочие.
 *
 * Отсюда форма стора: `load(eventId)` тянет **и** мероприятие, **и** состав,
 * и только после этого известно, что человеку можно.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ АДМИНА В СОСТАВЕ НЕТ — И ЭТО НЕ ОШИБКА
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `SYSTEM_ROLE_ADMIN` проходит любую проверку прав, **не состоя** в
 * мероприятии. Поэтому `myMemberRole` у него честно `null`, а `canManage`
 * всё равно `true`. Наивное `myMemberRole === 'MEMBER_ROLE_OWNER'` в шаблоне
 * спрятало бы от админа все кнопки на свете — и это ровно тот баг, который на
 * стенде не воспроизводится, потому что разработчик заходит админом и видит
 * всё... пока не проверит `canManage`, а не роль.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ПРАВ ЗДЕСЬ ДВА, А НЕ ОДНО
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `canManage` — «распоряжаюсь мероприятием». `canEditScanFields` — «могу
 * писать значение графы, ПОДСВЕЧЕННОЙ для сканирования». Второе появилось
 * вместе с ролью «Наблюдатель+» и НЕ является ослабленным первым: это другой
 * вопрос, у него другой ответ и другая половина условия (само поле). Оба
 * геттера перечисляют роли поимённо; сравнений вида `role >= …` в этом сторе
 * нет и быть не должно — у новой роли номер 3, и по порядку она стоит ПОСЛЕ
 * обычного наблюдателя.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ 404, А НЕ 403, НА ЧУЖОЕ МЕРОПРИЯТИЕ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Не-участник получает `EVENT_NOT_FOUND`, а не `FORBIDDEN`: 403 подтверждал бы
 * существование id и превращал бы перебор в перепись мероприятий. Значит, стор
 * не может отличить «id опечатка» от «меня не добавили» — и не должен пытаться.
 * Он кладёт ошибку в `error`, а вью показывает текст, называющий обе причины.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПОЧЕМУ ЭТО ОДИН СТОР, А НЕ ДВА
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Мероприятие и состав грузятся вместе и живут ровно столько, сколько человек
 * находится внутри мероприятия. Разнесённые по двум сторам, они дали бы
 * состояние «мероприятие есть, роль ещё не приехала», в котором `canManage`
 * равен `false` — то есть кнопки успевают моргнуть недоступными у настоящего
 * владельца. Здесь такого состояния нет: `ready` становится `true` только
 * когда известно и то, и другое.
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { eventsApi } from '@/api/events'
import type { ApiError } from '@/lib/api-error'
import { normalizeError } from '@/lib/api-error'
import { useAuthStore } from '@/stores/auth'
import type {
  CreateEventMemberUserRequest,
  Event,
  EventMember,
  MemberRole,
} from '@/types/api'

export const useEventStore = defineStore('event', () => {
  const auth = useAuthStore()

  /* ═══════════════════════════ state ═══════════════════════════ */

  /** Id мероприятия, которое сейчас открыто. `null` — мы вне скоупа мероприятия. */
  const eventId = ref<string | null>(null)

  const event = ref<Event | null>(null)

  /** Состав. Пагинации нет — это единицы человек. */
  const members = ref<EventMember[]>([])

  const loading = ref(false)

  /**
   * Ошибка загрузки скоупа. `shallowRef`, а не `ref`: `ApiError` — экземпляр
   * класса, и глубокий реактивный прокси над ним ломает `instanceof`
   * у потребителей.
   */
  const error = shallowRef<ApiError | null>(null)

  /**
   * Мероприятие и состав загружены (успешно или нет). До этого момента вью не
   * имеет права решать, что человеку можно: `canManage` ещё `false` не потому,
   * что прав нет, а потому что ответ не приехал.
   */
  const ready = ref(false)

  /* ═══════════════════════════ getters ═════════════════════════ */

  /**
   * Моя строка в составе. `null` и у админа (он в составе не числится), и у
   * того, кого ещё не добавили, — различать эти два случая должен `canManage`,
   * а не вызывающий код.
   */
  const myMembership = computed<EventMember | null>(() => {
    const userId = auth.user?.id
    if (!userId) return null
    return members.value.find((member) => member.userId === userId) ?? null
  })

  const myMemberRole = computed<MemberRole | null>(() => myMembership.value?.role ?? null)

  /**
   * Мероприятие мне видно. Отдельно от `canManage`, потому что наблюдатель
   * видит всё то же самое, но не может ничего изменить.
   */
  const canView = computed(() => auth.isAdmin || myMembership.value !== null)

  /**
   * ★ ЕДИНСТВЕННЫЙ ВОПРОС, КОТОРЫЙ НУЖНО ЗАДАВАТЬ ПЕРЕД ЛЮБОЙ КНОПКОЙ ВНУТРИ
   * МЕРОПРИЯТИЯ: «можно ли здесь писать».
   *
   * Считается через `||` с `isAdmin`, а не сравнением ролей, ровно по причине
   * из шапки: админа в составе нет. Сравнение перечисляет допустимые значения
   * явно и **не** использует порядок enum'а — добавление третьей роли в
   * середину списка не должно молча раздать ей права владельца.
   *
   * ⚠️ Это аффорданс UI. Авторитет — сервер: `guard.RequireManage` ответит 403
   * наблюдателю независимо от того, что решил фронт.
   */
  const canManage = computed(() => auth.isAdmin || myMemberRole.value === 'MEMBER_ROLE_OWNER')

  /**
   * ★ ВТОРОЙ И ПОСЛЕДНИЙ ВОПРОС ПРО ПРАВА В МЕРОПРИЯТИИ: «можно ли мне трогать
   * графы, подсвеченные для сканирования».
   *
   * Отдельно от `canManage`, потому что это про другое. `canManage` — «я
   * распоряжаюсь мероприятием»; здесь — «я стою на двери и отмечаю то, что на
   * двери и проверяют». У «Наблюдателя+» второе есть, первого нет.
   *
   * ⚠️ ЭТО ТОЛЬКО ПОЛОВИНА ОТВЕТА. Вторая половина — само поле: писать можно
   * лишь то, у которого `highlightOnScan`. Сервер складывает половины в
   * `EventGuard.RequireScanFieldWrite`; UI обязан складывать их так же —
   * `canEditScanFields && field.highlightOnScan`. Прочитать один этот геттер и
   * решить «значит, можно править всё» — это выдать «наблюдателю+» все
   * дополнительные поля мероприятия разом.
   *
   * ⚠️ Роли перечислены ПОИМЁННО, и это не стиль. Соблазн написать
   * `role >= VIEWER_PLUS` появляется ровно с третьей ролью — а её номер в
   * proto равен 3, то есть по порядку она стоит ПОСЛЕ обычного наблюдателя,
   * и такое сравнение раздало бы права ровно наоборот.
   *
   * ⚠️ Право отзывается снятием подсветки с поля, а не сменой роли. Экран,
   * открытый до снятия, узнает об этом из 403 — и обязан это показать, а не
   * молча проглотить (см. `ParticipantForm` и `ParticipantsListView`).
   */
  const canEditScanFields = computed(
    () => auth.isAdmin
      || myMemberRole.value === 'MEMBER_ROLE_OWNER'
      || myMemberRole.value === 'MEMBER_ROLE_VIEWER_PLUS',
  )

  /**
   * Владельцы мероприятия — к ним идут за доступом. Наблюдателю показываем их
   * прямо на экране «недостаточно прав»: сообщение «обратитесь к владельцу»
   * без имени владельца бесполезно.
   */
  const owners = computed(() => members.value.filter((m) => m.role === 'MEMBER_ROLE_OWNER'))

  /** Название для шапки и хлебных крошек. Пустая строка, пока не загрузились. */
  const eventName = computed(() => event.value?.name ?? '')

  /* ═══════════════════════════ загрузка ════════════════════════ */

  /**
   * Контроллер текущего полёта. Модульная переменная, а не `ref`: реактивность
   * ему не нужна, а `reactive`-прокси над `AbortController` ломает `abort()`.
   */
  let controller: AbortController | null = null

  /**
   * Загрузить скоуп мероприятия.
   *
   * Мероприятие и состав тянутся **параллельно**: это два независимых запроса,
   * и последовательный вызов удваивал бы задержку перед первым отрисованным
   * экраном на ровном месте.
   *
   * Никогда не бросает: ошибка оседает в `error`.
   *
   * @param id     мероприятие
   * @param force  перезагрузить, даже если это то же самое мероприятие. Нужно
   *               после правки состава — роль могли поменять прямо сейчас.
   */
  async function load(id: string, force = false): Promise<void> {
    if (!force && eventId.value === id && ready.value && error.value === null) return

    controller?.abort()
    const ctl = new AbortController()
    controller = ctl

    // Смена мероприятия обязана СРАЗУ обнулить старые данные: иначе между двумя
    // мероприятиями секунду виден чужой состав, а вместе с ним и чужая роль —
    // то есть кнопки, которых в новом мероприятии может не быть.
    if (eventId.value !== id) {
      event.value = null
      members.value = []
      ready.value = false
    }

    eventId.value = id
    loading.value = true
    error.value = null

    try {
      const [eventResponse, membersResponse] = await Promise.all([
        eventsApi.get(id, ctl.signal),
        eventsApi.listMembers(id, ctl.signal),
      ])

      // Ответ не последнего полёта: пользователь успел уйти в другое
      // мероприятие, и записывать сюда его данные нельзя.
      if (controller !== ctl) return

      event.value = eventResponse.event
      members.value = membersResponse.members
    } catch (err) {
      if (controller !== ctl) return

      const apiError = normalizeError(err)
      if (apiError.isCanceled) return

      error.value = apiError
      event.value = null
      members.value = []
    } finally {
      if (controller === ctl) {
        controller = null
        loading.value = false
        ready.value = true
      }
    }
  }

  /** Перечитать только состав — после добавления, смены роли или исключения. */
  async function reloadMembers(): Promise<void> {
    const id = eventId.value
    if (!id) return

    const response = await eventsApi.listMembers(id)
    members.value = response.members
  }

  /* ═══════════════════════════ мутации ═════════════════════════

     Все три **бросают** `ApiError`: на экране состава уже есть данные, и
     правильная реакция на отказ — тост, а не подмена таблицы красным
     прямоугольником. Вызывающий заворачивает в `useAsyncAction`.

     Каждая заканчивается перечитыванием состава, а не локальной правкой
     массива: своя роль считается из этого же списка, и разошедшаяся копия
     означала бы кнопки, которых у человека уже нет. */

  async function addMember(userId: string, role: MemberRole): Promise<EventMember> {
    const id = requireEventId()
    const response = await eventsApi.addMember(id, { userId, role })
    await reloadMembers()
    return response.member
  }

  /**
   * Завести новую учётную запись и сразу включить её в состав.
   *
   * ⚠️ Тело не несёт системной роли — её нет в контракте: созданная запись
   * всегда обычный пользователь, а `role` относится к мероприятию. Не
   * добавляйте сюда системную роль «для симметрии с формой пользователей»:
   * ровно её отсутствие и позволяет владельцу пользоваться этим методом.
   *
   * Перечитывание состава здесь особенно важно: строка приезжает в ответе, но
   * своя роль считается из общего списка, и разошедшаяся копия означала бы
   * кнопки, которых у человека уже нет.
   */
  async function createMemberUser(body: CreateEventMemberUserRequest): Promise<EventMember> {
    const id = requireEventId()
    const response = await eventsApi.createMemberUser(id, body)
    await reloadMembers()
    return response.member
  }

  /**
   * ⚠️ `409 CANNOT_MODIFY_SELF`: владелец не может понизить сам себя — он
   * немедленно потеряет доступ, которым только что распоряжался. Интерфейс
   * обязан такую кнопку не показывать, но проверка на сервере всё равно есть:
   * между отрисовкой и кликом состав мог измениться.
   */
  async function setMemberRole(userId: string, role: MemberRole): Promise<EventMember> {
    const id = requireEventId()
    const response = await eventsApi.setMemberRole(id, userId, { role })
    await reloadMembers()
    return response.member
  }

  async function removeMember(userId: string): Promise<void> {
    const id = requireEventId()
    await eventsApi.removeMember(id, userId)
    await reloadMembers()
  }

  /**
   * Мероприятие изменилось (переименовали, сдвинули даты, сменили статус) —
   * положить новую версию, не перезапрашивая состав.
   */
  function applyEvent(next: Event): void {
    event.value = next
    if (eventId.value === next.id) return
    // Сюда попасть нельзя, но если попали — молчать хуже, чем ругаться:
    // рассинхрон id и данных означает, что экран показывает не то мероприятие.
    console.warn('[event] applyEvent для другого мероприятия', next.id, eventId.value)
  }

  function requireEventId(): string {
    const id = eventId.value
    if (!id) throw new Error('[event] мутация состава вне скоупа мероприятия')
    return id
  }

  /* ═══════════════════════════ $reset ══════════════════════════

     ⚠️ У setup-сторов **нет** бесплатного `$reset()` — дефолт Pinia бросает.
     Возвращённая отсюда функция перекрывает его. Зовётся из `auth.reset()`,
     который обходит реестр пинии: без этого следующий вошедший первую секунду
     видел бы чужое мероприятие И чужую роль в нём. */

  function $reset(): void {
    controller?.abort()
    controller = null

    eventId.value = null
    event.value = null
    members.value = []
    loading.value = false
    error.value = null
    ready.value = false
  }

  return {
    // state
    eventId,
    event,
    members,
    loading,
    error,
    ready,
    // getters
    myMembership,
    myMemberRole,
    canView,
    canManage,
    canEditScanFields,
    owners,
    eventName,
    // actions
    load,
    reloadMembers,
    addMember,
    createMemberUser,
    setMemberRole,
    removeMember,
    applyEvent,
    $reset,
  }
})
